import DynamicChart from "./DynamicChart";
import { useParams } from "react-router";
import { useModuleContext } from "../context/ModuleContext";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import TypingLoader from "./common/TypingLoader";
import { submitChatFeedback } from "../services/chatService";
import { fetchCitations } from "../services/citationService";
import { ThumbsUp, ThumbsDown, VolumeX, Volume2, FileText, ClipboardCheck, Clipboard, Bot, X, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Modal from './common/Modal';
import { getUserIdFromToken } from "../utils/authUtils";
import JsonTable from "./JsonTable";
import CitationsModal from "./modals/CitationsModal";
import { Fragment } from "react";
import ACGIconLogo from "../assets/acg-logo-invert.png"

export const markdownComponents = {
    code({ className, children, ...props }) {
        const language = className?.replace('language-', '') || '';

        // Handle `chart` language specially
        if (language === 'chart') {
            const chartCode = String(children).trim();

            // Don't try to parse if the code is obviously incomplete (common during streaming)
            if (!chartCode || chartCode.length < 10) {
                return <DynamicChart code={chartCode} initialLoading={true} />;
            }

            try {
                const parsedChart = JSON.parse(chartCode);
                return <DynamicChart code={parsedChart} />;
            } catch (error) {
                // During streaming, incomplete JSON is normal - show loading instead of error
                // Only show error for obviously complete but invalid JSON
                const errorMessage = error.message || '';

                // Check if this looks like a streaming error (incomplete JSON)
                if (errorMessage.includes('Unterminated') ||
                    errorMessage.includes('Unexpected end') ||
                    errorMessage.includes('position') ||
                    chartCode.includes('```') === false) {
                    // Likely streaming - show loading
                    return <DynamicChart code={chartCode} initialLoading={true} />;
                }

                // If it looks like complete but invalid JSON, show error after a delay
                return <DynamicChart code={chartCode} initialLoading={true} />;
            }
        }

        // Distinguish between inline code (no className) and code blocks (with className)
        if (!className) {
            // Inline code - render as inline element
            return (
                <code
                    style={{
                        backgroundColor: '#1F2937',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.875em',
                        color: '#F3F4F6',
                        display: 'inline',
                    }}
                    {...props}
                >
                    {children}
                </code>
            );
        }

        // Block code - render as block element with pre wrapper
        return (
            <pre
                style={{
                    backgroundColor: '#1F2937',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    overflowX: 'auto',
                    color: '#F3F4F6',
                }}
            >
                <code className={className} {...props}>
                    {children}
                </code>
            </pre>
        );
    },
    p({ children }) {
        return (
            <p style={{ marginBottom: '1rem', color: '#F3F4F6' }}>
                {children}
            </p>
        );
    },
    strong({ children }) {
        return (
            <strong style={{ fontWeight: 700, color: '#93C5FD' }}>
                {children}
            </strong>
        );
    },
    ul({ children }) {
        return (
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', color: '#F3F4F6', listStyleType: 'disc' }}>
                {children}
            </ul>
        );
    },
    li({ children }) {
        return (
            <li style={{ marginBottom: '0.5rem' }}>
                {children}
            </li>
        );
    },
    table({ children }) {
        return (
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', borderSpacing: 0 }}>
                    {children}
                </table>
            </div>
        );
    },
    thead({ children }) {
        return (
            <thead style={{ backgroundColor: '#374151' }}>
                {children}
            </thead>
        );
    },
    tbody({ children }) {
        return (
            <tbody style={{ borderTop: `1px solid #4B5563` }}>
                {children}
            </tbody>
        );
    },
    tr({ children }) {
        return <tr>{children}</tr>;
    },
    td({ children }) {
        return (
            <td
                style={{
                    padding: '1rem 1.5rem',
                    whiteSpace: 'pre-wrap',
                    color: '#F3F4F6',
                    borderBottom: '1px solid #4B5563',
                    fontSize: '0.75rem'
                }}
            >
                {children}
            </td>
        );
    },
    th({ children }) {
        return (
            <th
                style={{
                    padding: '0.75rem 1.5rem',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#D1D5DB',
                }}
            >
                {children}
            </th>
        );
    },
};

function isJsonString(str) {
    if (!str || typeof str !== 'string') {
        return false;
    }
    
    // Remove any leading/trailing whitespace
    const trimmed = str.trim();
    
    // Check if it's wrapped in markdown code blocks and extract if so
    let jsonStr = trimmed;
    if (trimmed.startsWith('```json\n') && trimmed.endsWith('\n```')) {
        jsonStr = trimmed.slice(8, -4).trim(); // Remove ```json\n and \n```
    } else if (trimmed.startsWith('```\n') && trimmed.endsWith('\n```')) {
        jsonStr = trimmed.slice(4, -4).trim(); // Remove ```\n and \n```
    }
    
    // Must start with { or [ to be JSON object/array
    if (!jsonStr.startsWith('{') && !jsonStr.startsWith('[')) {
        return false;
    }
    
    try {
        const parsed = JSON.parse(jsonStr);
        return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
        return false;
    }
}

function hasFocus360Shape(obj) {
    if (!obj || typeof obj !== 'object') {
        return false;
    }
    return (
        obj.pending_payments &&
        obj.dispatch_status &&
        obj.external &&
        typeof obj.pending_payments === 'object' &&
        typeof obj.dispatch_status === 'object' &&
        typeof obj.external === 'object'
    );
}

/** True if root or nested `data` has Focus360 insight sections (insights API envelope or flat payload). */
function isFocus360JsonStructure(parsedJson) {
    if (!parsedJson || typeof parsedJson !== 'object') {
        return false;
    }
    return hasFocus360Shape(parsedJson) || hasFocus360Shape(parsedJson.data);
}

/** Inner context_data for UI; envelope fields (customer_name, location) stay on the full parsed object. */
function getFocus360InnerPayload(parsedJson) {
    if (!parsedJson || typeof parsedJson !== 'object') {
        return null;
    }
    if (hasFocus360Shape(parsedJson.data)) {
        return parsedJson.data;
    }
    if (hasFocus360Shape(parsedJson)) {
        return parsedJson;
    }
    return null;
}

function extractJsonFromString(str) {
    if (!str || typeof str !== 'string') {
        return null;
    }
    
    // Remove any leading/trailing whitespace
    const trimmed = str.trim();
    
    // Check if it's wrapped in markdown code blocks and extract if so
    let jsonStr = trimmed;
    if (trimmed.startsWith('```json\n') && trimmed.endsWith('\n```')) {
        jsonStr = trimmed.slice(8, -4).trim(); // Remove ```json\n and \n```
    } else if (trimmed.startsWith('```\n') && trimmed.endsWith('\n```')) {
        jsonStr = trimmed.slice(4, -4).trim(); // Remove ```\n and \n```
    }
    
    try {
        return JSON.parse(jsonStr);
    } catch (e) {
        return null;
    }
}

export default function BotMessage({
    content,
    citations = [],
    images = [],
    pairId,
    feedback,
    onJsonData,
    reportCustomerName,
    reportLocation,
    reportSiteInfo,
}) {
    const { id: chatId } = useParams();
    const { selectedModule } = useModuleContext();
    const [selectedImage, setSelectedImage] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackReason, setFeedbackReason] = useState('');
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speechSynthesis, setSpeechSynthesis] = useState(null);
    const [isIntentionalStop, setIsIntentionalStop] = useState(false);

    // Local feedback state for immediate UI updates
    const [localFeedback, setLocalFeedback] = useState(feedback);

    // Citations state
    const [showCitationsModal, setShowCitationsModal] = useState(false);
    const [citationsData, setCitationsData] = useState([]);
    const [isLoadingCitations, setIsLoadingCitations] = useState(false);

    // Accordion state for Focus360 sections
    const [accordionState, setAccordionState] = useState({
        pendingPayments: false,
        dispatchStatus: false,
        customerVisits: false,
        salesOpportunities: false,
        externalInsights: false
    });

    const isLoading = content === '___LOADING___';

    // Toggle accordion sections
    const toggleAccordion = (section) => {
        setAccordionState(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Accordion component
    const AccordionSection = ({ title, isOpen, onToggle, children }) => (
        <div className="border border-slate-600 rounded-lg mb-4">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-4 py-2 text-left hover:bg-slate-800/50 transition-colors rounded-t-lg cursor-pointer"
            >
                <h3 className="text-lg font-medium text-white !mt-0">{title}</h3>
                {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen && (
                <div className="p-4 py-8 pt-4 border-t border-slate-600">
                    <div className="focus360-accordion-content">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );

    const isJson = isJsonString(content);
    const parsedJSON = isJson ? extractJsonFromString(content) : null;
    
    // Determine if this should render as Focus360 UI
    const isFocus360Json = selectedModule === 'focus360' && 
                          isJson && 
                          parsedJSON && 
                          isFocus360JsonStructure(parsedJSON);

    const focus360Inner = isFocus360Json ? getFocus360InnerPayload(parsedJSON) : null;

    // // Debug logging for focus360 content detection
    // useEffect(() => {
    //     if (selectedModule === 'focus360') {
    //         console.log(`BotMessage Debug - Focus360 Content:`, {
    //             isJson,
    //             isFocus360Json,
    //             contentLength: content?.length,
    //             contentStart: content?.substring(0, 100),
    //             hasValidParsedJSON: !!parsedJSON,
    //             hasCorrectStructure: parsedJSON ? isFocus360JsonStructure(parsedJSON) : false
    //         });
    //     }
    // }, [content, selectedModule]);

    // Pass JSON data to parent (Focus360); merge report fields from message metadata when present
    useEffect(() => {
        if (selectedModule !== 'focus360' || !content || !onJsonData) {
            return;
        }
        if (!isJsonString(content)) {
            return;
        }
        const parsed = extractJsonFromString(content);
        if (!parsed || !isFocus360JsonStructure(parsed)) {
            return;
        }
        const merged = { ...parsed };
        if (typeof reportCustomerName === 'string' && reportCustomerName.trim()) {
            merged.customer_name = reportCustomerName.trim();
        }
        if (typeof reportLocation === 'string' && reportLocation.trim()) {
            merged.location = reportLocation.trim();
        }
        if (Array.isArray(reportSiteInfo) && reportSiteInfo.length > 0) {
            merged.site_info = reportSiteInfo;
        }
        onJsonData(merged);
    }, [content, reportCustomerName, reportLocation, reportSiteInfo, selectedModule, onJsonData]);

    // Initialize speech synthesis
    useEffect(() => {
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            setSpeechSynthesis(window.speechSynthesis);
        }
    }, []);

    // Cleanup speech synthesis on unmount
    useEffect(() => {
        return () => {
            if (speechSynthesis && isPlaying) {
                try {
                    setIsIntentionalStop(true);
                    speechSynthesis.cancel();
                } catch (error) {
                    console.error('Error cleaning up speech on unmount:', error);
                }
            }
        };
    }, [speechSynthesis, isPlaying]);

    // Update local feedback when prop changes (for loaded messages)
    useEffect(() => {
        setLocalFeedback(feedback);
    }, [feedback]);

    const handleTextToSpeech = () => {
        if (!speechSynthesis) {
            toast.error('Text-to-speech not supported in this browser');
            return;
        }

        if (isPlaying) {
            // Stop current speech
            try {
                setIsIntentionalStop(true);
                speechSynthesis.cancel();
                setIsPlaying(false);
                // Reset the intentional stop flag after a short delay
                setTimeout(() => setIsIntentionalStop(false), 100);
            } catch (error) {
                console.error('Error stopping speech:', error);
                setIsPlaying(false);
                setIsIntentionalStop(false);
            }
            return;
        }

        // Clean content for better speech (remove markdown, JSON formatting, etc.)
        let textToSpeak = content;
        if (isFocus360Json && focus360Inner) {
            // For Focus360 JSON responses, speak the summaries instead
            const summaries = [
                focus360Inner.pending_payments?.pending_payments_summary,
                focus360Inner.dispatch_status?.dispatch_status_summary,
                focus360Inner.external?.external_insights
            ].filter(Boolean);
            textToSpeak = summaries.join('. ');
        }

        // Clean markdown formatting
        textToSpeak = textToSpeak
            .replace(/```[\s\S]*?```/g, '') // Remove code blocks
            .replace(/`([^`]+)`/g, '$1') // Remove inline code formatting
            .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold formatting
            .replace(/\*(.*?)\*/g, '$1') // Remove italic formatting
            .replace(/#{1,6}\s/g, '') // Remove headings
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
            .replace(/\n+/g, '. ') // Replace newlines with periods
            .trim();

        if (!textToSpeak) {
            toast.error('No text content to read');
            return;
        }

        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 0.8;

        utterance.onstart = () => {
            setIsPlaying(true);
        };

        utterance.onend = () => {
            setIsPlaying(false);
        };

        utterance.onpause = () => {
            setIsPlaying(false);
        };

        utterance.onresume = () => {
            setIsPlaying(true);
        };

        utterance.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            setIsPlaying(false);

            // Don't show error toast for intentional interruptions
            if (event.error === 'interrupted' && isIntentionalStop) {
                console.log('Speech intentionally stopped by user');
                return;
            }

            // Only show error toast for actual errors
            if (event.error !== 'interrupted') {
                toast.error('Error occurred during text-to-speech');
            }
        };

        try {
            speechSynthesis.speak(utterance);
        } catch (error) {
            console.error('Error starting speech:', error);
            setIsPlaying(false);
            toast.error('Failed to start text-to-speech');
        }
    };

    if (isLoading) {
        return <TypingLoader />;
    }

    const openImageModal = (imageUrl) => setSelectedImage(imageUrl);
    const closeImageModal = () => setSelectedImage(null);

    const handleFeedbackSubmit = async () => {
        setIsSubmittingFeedback(true);
        try {
            if (!chatId || !pairId) {
                console.error('Chat ID or Pair ID missing');
                toast.error('Unable to submit feedback. Please try again.');
                return;
            }

            const hasExistingFeedback = localFeedback !== null;
            await submitChatFeedback(chatId, pairId, false, feedbackReason, hasExistingFeedback, selectedModule);

            // Immediately update local feedback state for instant UI feedback
            setLocalFeedback({
                hasThumbsUp: false,
                hasThumbsDown: true,
                feedbackText: feedbackReason,
                timestamp: new Date().toISOString()
            });

            setShowFeedbackModal(false);
            setFeedbackReason('');

            toast.success('👎 Feedback submitted successfully!', {
                duration: 3000,
                style: {
                    background: '#EF4444',
                    color: 'white',
                },
            });
        } catch (error) {
            console.error('Feedback submission failed:', error);
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleThumbsUp = async () => {
        try {
            if (!chatId || !pairId) {
                console.error('Chat ID or Pair ID missing');
                toast.error('Unable to submit feedback. Please try again.');
                return;
            }

            const hasExistingFeedback = localFeedback !== null;
            await submitChatFeedback(chatId, pairId, true, '', hasExistingFeedback, selectedModule);

            // Immediately update local feedback state for instant UI feedback
            setLocalFeedback({
                hasThumbsUp: true,
                hasThumbsDown: false,
                feedbackText: '',
                timestamp: new Date().toISOString()
            });

            toast.success('👍 Feedback submitted successfully!', {
                duration: 3000,
                style: {
                    background: '#10B981',
                    color: 'white',
                },
            });
        } catch (error) {
            console.error('Thumbs up feedback failed:', error);
            toast.error('Failed to submit feedback. Please try again.');
        }
    };

    const handleFeedbackCancel = () => {
        setShowFeedbackModal(false);
        setFeedbackReason('');
    };


    const handleCitationsClick = async () => {
        if (!chatId || !pairId) {
            toast.error('Unable to fetch citations. Missing chat or pair ID.');
            return;
        }

        const userId = getUserIdFromToken();
        if (!userId) {
            toast.error('Unable to fetch citations. User not authenticated.');
            return;
        }

        setIsLoadingCitations(true);
        setShowCitationsModal(true);

        try {
            const citationsResponse = await fetchCitations(chatId, userId, pairId);
            setCitationsData(citationsResponse);
        } catch (error) {
            console.error('Failed to fetch citations:', error);
            toast.error('Failed to load citations. Please try again.');
            setCitationsData([]);
        } finally {
            setIsLoadingCitations(false);
        }
    };

    const copyToClipboardFallback = (text) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed'; // Prevent scrolling to bottom of page
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            console.log(successful ? 'Copy success' : 'Copy failed');
        } catch (err) {
            console.error('Fallback copy failed', err);
        }

        document.body.removeChild(textArea);
    };

    if (isFocus360Json && focus360Inner) {
        return (
            <div className="flex w-full h-fit gap-2 text-lg leading-7">
                <div className="bg-transparent rounded-full h-fit p-1">
                {/* <Bot size={20} /> */}
                <img src={ACGIconLogo} alt="ACG Icon" className="w-[28px] h-[28px] object-contain" />
            </div>
                <div className="flex-1 pb-4">
                    {/* UI-visible Content */}
                    <div className="prose prose-sm max-w-none ui-only pb-8">
                        {/* Aging Table - Always visible (no accordion) */}
                        <div className="mb-6">
                            <p className="font-medium mb-2 text-white">Pending Payment Ageing Table</p>
                            {focus360Inner.pending_payments?.pending_payments_aging_table ? 
                                <JsonTable data={focus360Inner.pending_payments.pending_payments_aging_table} /> : 
                                <p>No data available</p>
                            }
                        </div>

                        {/* Pie chart if available */}
                        {focus360Inner.pending_payments?.pending_payments_pie_chart_url && (
                            <img
                                className="my-3 mt-5"
                                src={focus360Inner.pending_payments.pending_payments_pie_chart_url}
                                alt=""
                            />
                        )}

                        {/* Pending Payments Summary - Accordion */}
                        {focus360Inner.pending_payments?.pending_payments_summary && (
                            <AccordionSection
                                title="Pending Payments Summary"
                                isOpen={accordionState.pendingPayments}
                                onToggle={() => toggleAccordion('pendingPayments')}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{focus360Inner.pending_payments.pending_payments_summary}</ReactMarkdown>
                            </AccordionSection>
                        )}

                        {/* Dispatch Status - Accordion */}
                        {focus360Inner.dispatch_status?.dispatch_status_summary && (
                            <AccordionSection
                                title="Dispatch Delays"
                                isOpen={accordionState.dispatchStatus}
                                onToggle={() => toggleAccordion('dispatchStatus')}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{focus360Inner.dispatch_status.dispatch_status_summary}</ReactMarkdown>
                            </AccordionSection>
                        )}

                        {/* Customer Visits Summary - Accordion */}
                        {focus360Inner.mom_results?.customer_visits_summary && (
                            <AccordionSection
                                title="Customer Visits Summary"
                                isOpen={accordionState.customerVisits}
                                onToggle={() => toggleAccordion('customerVisits')}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{focus360Inner.mom_results.customer_visits_summary}</ReactMarkdown>
                            </AccordionSection>
                        )}

                        {/* Sales Opportunities - Accordion */}
                        {focus360Inner.opportunity_results?.opportunity_summary && (
                            <AccordionSection
                                title="Sales Opportunities"
                                isOpen={accordionState.salesOpportunities}
                                onToggle={() => toggleAccordion('salesOpportunities')}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{focus360Inner.opportunity_results.opportunity_summary}</ReactMarkdown>
                            </AccordionSection>
                        )}

                        {/* External Insights - Accordion */}
                        {focus360Inner.external?.external_insights && (
                            <AccordionSection
                                title="External Insights"
                                isOpen={accordionState.externalInsights}
                                onToggle={() => toggleAccordion('externalInsights')}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{focus360Inner.external.external_insights}</ReactMarkdown>
                            </AccordionSection>
                        )}

                        {/* Budget vs Actual - Accordion */}
                        {/* {parsedJSON.budget_vs_actual?.budget_vs_actual_summary && (
                            <AccordionSection
                                title="Budget vs Actual"
                                isOpen={accordionState.budgetVsActual}
                                onToggle={() => toggleAccordion('budgetVsActual')}
                            >
                                <ReactMarkdown>{parsedJSON.budget_vs_actual.budget_vs_actual_summary}</ReactMarkdown>
                            </AccordionSection>
                        )} */}

                        {/* Customer Complaints - Accordion */}
                        {/* {parsedJSON.customer_complaints?.customer_complaints_summary && (
                            <AccordionSection
                                title="Customer Complaints"
                                isOpen={accordionState.customerComplaints}
                                onToggle={() => toggleAccordion('customerComplaints')}
                            >
                                <ReactMarkdown>{parsedJSON.customer_complaints.customer_complaints_summary}</ReactMarkdown>
                            </AccordionSection>
                        )} */}
                    </div>

                    {selectedImage && (
                        <Modal open={!!selectedImage} setOpen={closeImageModal} modalWidth="90%">
                            <Fragment>
                                <button
                                    className="absolute right-2 top-2 bg-white size-7 rounded duration-300 hover:bg-red-100 text-red-500"
                                    onClick={closeImageModal}
                                >
                                    <X />
                                </button>
                                <img
                                    src={selectedImage}
                                    alt="Full preview"
                                    className="w-full h-auto object-contain max-h-[80vh] mx-auto"
                                />
                            </Fragment>
                        </Modal>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex w-full h-fit gap-2 pb-8">
            {selectedImage && (
                        <Modal open={!!selectedImage} setOpen={closeImageModal} modalWidth="90%">
                            <Fragment>
                                <button
                                    className="absolute right-2 top-2 bg-white size-7 rounded duration-300 hover:bg-red-100 text-red-500"
                                    onClick={closeImageModal}
                                >
                                    <X />
                                </button>
                                <img
                                    src={selectedImage}
                                    alt="Full preview"
                                    className="w-full h-auto object-contain max-h-[80vh] mx-auto"
                                />
                            </Fragment>
                        </Modal>
            )}
            <div className="bg-transparent rounded-full h-fit p-1">
                {/* <Bot size={20} /> */}
                <img src={ACGIconLogo} alt="ACG Icon" className="w-[28px] h-[28px] object-contain" />
            </div>
            <div className="w-full prose prose-invert prose-sm max-w-none pt-2">
                <ReactMarkdown 
                    remarkPlugins={[remarkGfm]} 
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents}
                >
                    {content}
                </ReactMarkdown>
                {images.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                        {images.map((img, idx) => {
                            // Handle both old format (img.data) and new format (img.image_path)
                            const imageSrc = img.data || img.image_path;
                            const imageAlt = img.filename || `AI response image ${idx + 1}`;
                            
                            // Debug log for image rendering
                            console.log(`Rendering image ${idx + 1}:`, { imageSrc, imageAlt, img });
                            
                            return (
                                <div key={idx} className="relative">
                                    <img
                                        src={imageSrc}
                                        onClick={() => openImageModal(imageSrc)}
                                        alt={imageAlt}
                                        className="rounded border border-white/10 max-h-[250px] object-contain cursor-pointer"
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                {citations.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/20">
                        <p className="font-semibold">References:</p>
                        {citations.map((c, i) => (
                            <div key={i} className="border-l-2 border-white/40 pl-2">
                                <p className="font-medium">{c.filename}</p>
                            </div>
                        ))}
                    </div>
                )}
                <section className="flex gap-2 border-t border-white/20 pt-2 mt-2">
                    <button
                        onClick={handleThumbsUp}
                        className={`ml-auto transition-colors cursor-pointer ${localFeedback?.hasThumbsUp
                                ? 'text-green-500 hover:text-green-400 fill-green-500'
                                : 'text-white/50 hover:text-white/80'
                            }`}
                    >
                        <ThumbsUp size={20} />
                    </button>
                    <button
                        onClick={() => setShowFeedbackModal(true)}
                        className={`transition-colors cursor-pointer ${localFeedback?.hasThumbsDown
                                ? 'text-red-500 hover:text-red-400'
                                : 'text-white/50 hover:text-white/80'
                            }`}
                    >
                        <ThumbsDown size={20} />
                    </button>
                    <button
                        onClick={handleTextToSpeech}
                        className={`transition-colors cursor-pointer ${isPlaying
                                ? 'text-blue-400 hover:text-blue-300'
                                : 'text-white/50 hover:text-white/80'
                            }`}
                        title={isPlaying ? "Stop reading" : "Read aloud"}
                    >
                        {isPlaying ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                    {/* Citations button - only show for smart-assistant module */}
                    {selectedModule === 'smart-assistant' && pairId && (
                        <button
                            onClick={handleCitationsClick}
                            className="text-white/50 hover:text-white/80 transition-colors cursor-pointer"
                            title="View citations"
                        >
                            <FileText size={20} />
                        </button>
                    )}
                    <button
                        onClick={() => {
                            copyToClipboardFallback(content);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                        }}
                        className="text-white/50 hover:text-white/80 transition-colors cursor-pointer"
                        title="Copy content"
                    >
                        {copied ? <ClipboardCheck size={20} /> : <Clipboard size={20} />}
                    </button>
                </section>

                {/* Feedback Modal */}
                {showFeedbackModal && (
                    <Modal open={showFeedbackModal} setOpen={setShowFeedbackModal} modalWidth="400px" isDark={true}>
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-white mb-4">
                                Help us improve
                            </h3>
                            <p className="text-gray-300 text-sm mb-4">
                                Please let us know what went wrong with this response:
                            </p>
                            <textarea
                                value={feedbackReason}
                                onChange={(e) => setFeedbackReason(e.target.value)}
                                placeholder="Describe the issue..."
                                className="w-full h-24 p-3 bg-gray-800 text-white border border-gray-600 rounded-md resize-none focus:outline-none focus:border-blue-500"
                                disabled={isSubmittingFeedback}
                            />
                            <div className="flex gap-3 mt-4">
                                <button
                                    onClick={handleFeedbackCancel}
                                    className="flex-1 px-4 py-2 text-gray-300 border border-gray-600 rounded-md hover:bg-gray-800 transition-colors"
                                    disabled={isSubmittingFeedback}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFeedbackSubmit}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isSubmittingFeedback || !feedbackReason.trim()}
                                >
                                    {isSubmittingFeedback ? 'Sending...' : 'Send'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* Citations Modal */}
                <CitationsModal
                    isOpen={showCitationsModal}
                    onClose={() => setShowCitationsModal(false)}
                    citations={citationsData}
                    isLoading={isLoadingCitations}
                />
            </div>
        </div>
    );
}